import React from "react";
import { observable } from "mobx";
import { observer, inject } from "mobx-react";
import { Table, TableHead, TableCell, TableRow, TableBody } from "@material-ui/core";
import SimpleModal from "@src/Components/Common/SimpleModal/SimpleModal";
import { IManagementStore } from "Stores/Management/types";
import { IValuesStore } from "Stores/Values/types";
import { Handover } from "@src/Shared/Handovers/Handover";
import ResultsAreEmpty from "@src/Components/Ui/ResultsAreEmpty";
import { STTableHeadTypography } from "@src/Components/Ui/STTableHeadTypography";
import { Values } from "@src/Tools/Values";
import { STEmptyAdd, CSTPaper } from "../CrewTable/styles";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import { Location } from "@src/Interfaces/Entity/Location";
import { HandoverCandidate } from "@src/Interfaces/Entity/Handover";
import { STWrapper, STTableRow } from "./styles";
import TransferWithinAStation from "@material-ui/icons/TransferWithinAStation";
import Cancel from "@material-ui/icons/Cancel";
import { STFabTooltip } from "@src/Components/Ui/STFab";
import { CrewPosition } from "@src/Interfaces/Entity/Crew";
import { IAuthStore } from "@src/Stores/Auth/types";
import { Toast } from "@src/Tools/Toast";
import { STTableCellTypography } from "@src/Components/Ui/STTableCellTypography";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";

interface IProps {
  auth?: IAuthStore;
  management?: IManagementStore;
  values?: IValuesStore;
  opened?: boolean;
  handleClose?: () => void;
  position?: CrewPosition;
  location: Location;
}

@inject("auth", "management", "values")
@observer
class CrewPersonModal extends React.Component<IProps> {
  @observable handoverAvailablePersons: HandoverCandidate[] = [];
  toast: Toast = new Toast();

  async componentDidMount() {
    const { position, location } = this.props;
    this.handoverAvailablePersons = await this.props.management.getHandoverCandidatesByPosition(
      position.id,
      location.id
    );
  }

  getTitle = (positionName: string, locationName: string) => {
    return Values.getLocalizationValue("handoverAvailablePersonsForPosition")
      .replace("{0}", positionName)
      .replace("{1}", locationName);
  };

  getIsActingColumnTitle = (positionName: string) => {
    return Values.getLocalizationValue("isActingPosition").replace("{0}", positionName);
  };

  handleHandoverButtonClick = async (handoverToPersonId: string) => {
    const { position, auth, location } = this.props;
    let handover = new Handover();
    handover.location = location.id;
    handover.position = position.id;
    handover.createdBy = auth.user.person.id;
    handover.handoverFrom =
      this.handoverAvailablePersons.find(person => person.activelyAssigned)?.id || this.props.values.common.EmptyId;
    handover.handoverTo = handoverToPersonId;
    const isSuccessRequest: boolean = await this.props.management.createHandover(handover);
    if (isSuccessRequest) {
      this.toast.showSuccess(Values.getLocalizationValue("handoverRequestCreated"));
      this.reloadComponent();
    }
  };

  handleRejectHandoverButtonClick = async (handoverToPersonId: string) => {
    const { location, position } = this.props;
    const isSuccessRequest: boolean = await this.props.management.withdrawHandoverByPerson(
      handoverToPersonId,
      location.id,
      position.id
    );
    if (isSuccessRequest) {
      this.toast.showInfo(Values.getLocalizationValue("withdrawHandover"));
      this.reloadComponent();
    }
  };

  reloadComponent = async () => {
    const { position, location } = this.props;
    this.handoverAvailablePersons = await this.props.management.getHandoverCandidatesByPosition(
      position.id,
      location.id
    );
    this.forceUpdate();
  };

  render() {
    const { auth, opened, handleClose, position, location } = this.props;
    const isAssociatedLocation = auth.user.location.id === location.id;
    return (
      <SimpleModal width={700} isOpened={opened} onClose={handleClose}>
        <STWrapper>
          <STFormTitleTypography align="center">{this.getTitle(position.name, location.name)}</STFormTitleTypography>
          <STContentWrapper>
            {this.handoverAvailablePersons.length === 0 && (
              <STEmptyAdd>
                <ResultsAreEmpty />
              </STEmptyAdd>
            )}
            {this.handoverAvailablePersons.length > 0 && (
              <CSTPaper elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <STTableHeadTypography>{Values.getLocalizationValue("person")}</STTableHeadTypography>
                      </TableCell>
                      <TableCell>
                        <STTableHeadTypography>{this.getIsActingColumnTitle(position.name)}</STTableHeadTypography>
                      </TableCell>
                      <TableCell>
                        <STTableHeadTypography>{Values.getLocalizationValue("lastAssignment")}</STTableHeadTypography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.handoverAvailablePersons.map((person: HandoverCandidate) => (
                      <STTableRow key={person.id}>
                        <TableCell size="small">
                          <STTableCellTypography>{person.fullName}</STTableCellTypography>
                        </TableCell>
                        <TableCell size="small">
                          <STTableCellTypography>
                            {person.activelyAssigned
                              ? Values.getLocalizationValue("yes")
                              : Values.getLocalizationValue("no")}
                          </STTableCellTypography>
                        </TableCell>
                        <TableCell size="small">
                          <STTableCellTypography>
                            {person.lastAssignment ? new Date(person.lastAssignment).toLocaleString() : ""}
                          </STTableCellTypography>
                        </TableCell>
                        <TableCell size="small">
                          {isAssociatedLocation && !person.activelyAssigned && !person.alreadyRequested && (
                            <STFabTooltip
                              color="primary"
                              tooltipTitle={Values.getLocalizationValue("handover")}
                              size="small"
                              onClick={() => this.handleHandoverButtonClick(person.id)}
                            >
                              <TransferWithinAStation />
                            </STFabTooltip>
                          )}
                          {isAssociatedLocation && !person.activelyAssigned && person.alreadyRequested && (
                            <STFabTooltip
                              color="secondary"
                              tooltipTitle={Values.getLocalizationValue("cancel")}
                              size="small"
                              onClick={() => this.handleRejectHandoverButtonClick(person.id)}
                            >
                              <Cancel />
                            </STFabTooltip>
                          )}
                        </TableCell>
                      </STTableRow>
                    ))}
                  </TableBody>
                </Table>
              </CSTPaper>
            )}
          </STContentWrapper>
        </STWrapper>
      </SimpleModal>
    );
  }
}

export default React.memo(CrewPersonModal);
